import React, { useEffect, useState, useCallback } from "react";
import AdminNav from "../components/Admin/AdminNav";
import { API_URL } from "../config";
import { authHeader, canManageProducts } from "../auth";
import styles from "./discounts.module.css";

const SOURCE_LABELS = {
  popup: "Pop-up",
  collections: "Collections page",
};

const Community = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const canManage = canManageProducts();

  const fetchSubscribers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/subscribers`, {
        headers: authHeader(),
      });
      if (!response.ok) throw new Error("Could not load the community list.");
      setSubscribers(await response.json());
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const flash = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 4000);
  };

  const copyAll = async () => {
    const emails = subscribers.map((s) => s.email).join(", ");
    try {
      await navigator.clipboard.writeText(emails);
      flash(`${subscribers.length} email${subscribers.length === 1 ? "" : "s"} copied — paste into BCC or your mail tool.`);
    } catch {
      setError("Could not copy — your browser blocked clipboard access.");
    }
  };

  const handleDelete = async (subscriber) => {
    if (!window.confirm(`Remove ${subscriber.email} from the community list?`)) return;
    setError("");
    try {
      const response = await fetch(`${API_URL}/subscribers/${subscriber._id || subscriber.id}`, {
        method: "DELETE",
        headers: authHeader(),
      });
      if (!response.ok) throw new Error("Could not remove the subscriber.");
      flash(`${subscriber.email} removed.`);
      fetchSubscribers();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className={styles.page}>
      <AdminNav />
      <div className={styles.inner}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Backoffice</span>
          <h1 className={styles.title}>Community</h1>
          <p className={styles.subtitle}>
            Everyone who registered their email on the storefront —{" "}
            {subscribers.length} member{subscribers.length === 1 ? "" : "s"} so far.
          </p>
        </header>

        {error && <p className={styles.error}>{error}</p>}
        {notice && <p className={styles.success}>{notice}</p>}

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Members</h2>
          {subscribers.length > 0 && (
            <button type="button" className={styles.submit} onClick={copyAll} style={{ marginTop: 0, marginBottom: "1rem" }}>
              Copy all emails
            </button>
          )}
          {loading ? (
            <p className={styles.loading}>Loading…</p>
          ) : subscribers.length === 0 ? (
            <p className={styles.loading}>
              No members yet — registrations from the site will appear here.
            </p>
          ) : (
            <ul className={styles.list}>
              {subscribers.map((subscriber) => (
                <li key={subscriber._id || subscriber.id} className={styles.row}>
                  <div className={styles.rowInfo}>
                    <span className={styles.code}>{subscriber.email}</span>
                    <span className={styles.rowLabel}>
                      {SOURCE_LABELS[subscriber.source] || subscriber.source || "Site"}
                      {" · "}
                      {new Date(subscriber.dateCreated).toLocaleDateString()}
                      {subscriber.welcomeEmailSent && " · welcomed ✓"}
                    </span>
                  </div>
                  {canManage && (
                    <div className={styles.actions}>
                      <button
                        type="button"
                        className={styles.dangerBtn}
                        onClick={() => handleDelete(subscriber)}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default Community;
